import { Box, Flex, InputV2, Text } from '@pinchenterprisesnpm/friday-ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDollarSign } from '@fortawesome/pro-solid-svg-icons'

import { hasBusinessPayRateUpdateRestriction } from '@src/helpers/businessEntityHelpers'
import { useRef } from 'react'
import { ISuggestedPayRateStrategy } from '@network/models/suggested-pay-rates.model'
import { faShieldCheck } from '@fortawesome/pro-light-svg-icons'
import { CustomValidationError } from '@src/features/shift-post-v2/style'
import {
  calculatePercentage,
  getSmartPaySetup,
} from '@src/features/shift-post-v2/components/smart-pay/smart-pay.utils'
import {
  InputWrapper,
  MarketRateGrid,
  NoneUserSelectText,
  PricingWrapper,
  RecommendedPayContainer,
  RecommendedPillContainer,
  SmartPayContainer,
} from './smart-pay.style'
import { BigTag, ProgressBar } from './components'
import { IStrategySelectionShiftParams } from './smart-pay.types'
import { getSelectedPayRateStrategy } from './smart-pay.utils'
import { useSmartPayTracking } from './tracking'

interface ISmartPayProps {
  payValue: string
  minPayRate: number
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void
  errorMessage?: string
  onPayRateChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  regularStrategy: ISuggestedPayRateStrategy
  lastMinuteStrategy: ISuggestedPayRateStrategy
  strategySelectionParams: IStrategySelectionShiftParams
}

const SmartPay = ({
  payValue,
  minPayRate,
  handleBlur,
  errorMessage,
  onPayRateChange,
  regularStrategy,
  lastMinuteStrategy,
  strategySelectionParams,
}: ISmartPayProps) => {
  const selectedPayRateStrategy = getSelectedPayRateStrategy(
    regularStrategy,
    lastMinuteStrategy,
    strategySelectionParams,
  )
  const recommendedPay = selectedPayRateStrategy.pay_rate
  const setup = getSmartPaySetup(Number(payValue), selectedPayRateStrategy)

  const inputKey = useRef<number>(0)

  const percentage = calculatePercentage(
    Number(payValue),
    minPayRate,
    recommendedPay,
  )
  const isPayRateRecommended = Number(payValue) >= recommendedPay

  // Split the tagline into lines by <br> tags
  const taglineLines = setup.tagline.split(/<br\s*\/?>/i)

  const { handleManualPayChange, markSmartPayClick } = useSmartPayTracking({
    payValue,
    recommendedPay,
    onPayRateChange,
  })

  return (
    <SmartPayContainer
      backgroundColor={setup.bgColor}
      py="md"
      px="sm"
      hasError={!!errorMessage}
    >
      <Flex justifyContent="space-between" alignItems="center">
        <Flex alignItems="flex-start">
          <InputWrapper>
            {isPayRateRecommended && (
              <RecommendedPillContainer px="8px" py="2px">
                <Text
                  my="0"
                  fontWeight="normal"
                  color="white"
                  fontSize="xsmall"
                >
                  RECOMMENDED
                </Text>
              </RecommendedPillContainer>
            )}
            <InputV2
              leftElement={
                <FontAwesomeIcon
                  icon={faDollarSign}
                  size="lg"
                  color="#C5C5C7"
                  style={{
                    height: 24,
                    width: 24,
                  }}
                />
              }
              labelStyles={{
                color: '#868686',
                fontWeight: 400,
              }}
              leftElementStyles={{
                top: 8,
                left: -1,
              }}
              numberArrowIconColor="gray.400"
              numberArrowsWrapperStyles={{
                right: 3,
              }}
              placeholder=""
              type="number"
              name="pay_value"
              id="pay_value"
              width="150px"
              value={payValue}
              isDisabled={hasBusinessPayRateUpdateRestriction()}
              onChange={handleManualPayChange}
              onBlur={handleBlur}
              hasError={!!errorMessage}
              onKeyDown={e => {
                if (['e', 'E', '-', '+'].includes(e.key)) {
                  e.preventDefault()
                }
              }}
              key={inputKey.current} // to force re-render the input when the pay rate is changed via the recommended pay button
            />
          </InputWrapper>

          {!isPayRateRecommended && (
            <PricingWrapper
              ml="md"
              onClick={() => {
                const event: React.ChangeEvent<HTMLInputElement> = {
                  target: {
                    value: recommendedPay.toString(),
                  },
                } as React.ChangeEvent<HTMLInputElement>
                onPayRateChange(event)
                inputKey.current++

                markSmartPayClick()
              }}
            >
              <RecommendedPayContainer
                alignItems="center"
                justifyContent="center"
                px="sm"
              >
                <Text my="0" pt="xs" fontWeight="semiBold">
                  ${recommendedPay}/hr
                </Text>
              </RecommendedPayContainer>

              <RecommendedPillContainer px="8px" py="2px">
                <Text
                  my="0"
                  fontWeight="normal"
                  color="white"
                  fontSize="xsmall"
                >
                  RECOMMENDED
                </Text>
              </RecommendedPillContainer>
            </PricingWrapper>
          )}
        </Flex>

        <Flex alignItems="center">
          <FontAwesomeIcon
            icon={faShieldCheck}
            size="xl"
            color={setup.messageColor}
          />

          <NoneUserSelectText my="0" color={setup.messageColor} ml="sm">
            {taglineLines.map((line, index) => (
              <>
                {line}
                {/* Add a <br /> after every line except the very last one */}
                {index < taglineLines.length - 1 && <br />}
              </>
            ))}
          </NoneUserSelectText>

          {setup.bigTag && (
            <BigTag
              ml="sm"
              color={setup.messageColor}
              text={setup.bigTag.text}
              icon={setup.bigTag.icon}
              bgColor={setup.bigTag.bgColor}
            />
          )}
        </Flex>
      </Flex>

      {errorMessage && (
        <CustomValidationError mt="xs">{errorMessage}</CustomValidationError>
      )}

      {Number(payValue) <= recommendedPay && (
        <Box mt="sm">
          <MarketRateGrid>
            <Text my="0" color="muted">
              Market rate for position
            </Text>

            <Text my="0" color="primary" fontWeight="semiBold">
              ${recommendedPay}/hr
            </Text>

            <ProgressBar percentage={percentage} color={setup.barColor} />
            <Box>{percentage}%</Box>
          </MarketRateGrid>
        </Box>
      )}
    </SmartPayContainer>
  )
}

export default SmartPay
